import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CSVUploadArea = ({ onFileUpload, isProcessing, existingPlays = [] }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('new'); // 'new' or 'update'
  const [selectedPlayId, setSelectedPlayId] = useState('');
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleFileSelection = (file) => {
    if (file && file?.name?.endsWith('.csv')) {
      setSelectedFile(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleUpload = () => {
    if (selectedFile && onFileUpload) {
      // Pass upload mode and selected play ID to parent
      onFileUpload(selectedFile, uploadMode, selectedPlayId);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const canUpload = selectedFile && (uploadMode === 'new' || (uploadMode === 'update' && selectedPlayId));

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Upload" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-card-foreground">Upload Shakespeare Play</h2>
          <p className="text-sm text-muted-foreground">Import CSV file using the specified schema format</p>
        </div>
      </div>
      {/* Upload Mode Selection */}
      <div className="mb-4 p-4 bg-muted/30 rounded-lg border border-border">
        <label className="block text-sm font-medium text-card-foreground mb-2">
          Upload Mode
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="uploadMode"
              value="new"
              checked={uploadMode === 'new'}
              onChange={(e) => setUploadMode(e?.target?.value)}
              className="w-4 h-4 text-primary"
            />
            <span className="text-sm text-card-foreground">Create New Play</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="uploadMode"
              value="update"
              checked={uploadMode === 'update'}
              onChange={(e) => setUploadMode(e?.target?.value)}
              className="w-4 h-4 text-primary"
            />
            <span className="text-sm text-card-foreground">Update Existing Play</span>
          </label>
        </div>

        {/* Play Selection for Update Mode */}
        {uploadMode === 'update' && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Select Play to Update
            </label>
            <Select
              value={selectedPlayId}
              onChange={(e) => setSelectedPlayId(e?.target?.value)}
              className="w-full"
            >
              <option value="">-- Select a play --</option>
              {existingPlays?.map(play => (
                <option key={play?.id} value={play?.id}>
                  {play?.title} ({play?.lineCount} lines)
                </option>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              <Icon name="Info" size={12} className="inline mr-1" />
              Existing lines and audio files will be preserved. Only new lines will be added.
            </p>
          </div>
        )}
      </div>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          isDragging
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
        } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!selectedFile ? (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Icon name="FileText" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-base font-medium text-card-foreground mb-2">
              Drag and drop your CSV file here
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse from your computer
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
              className="hidden"
              id="csv-file-input"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
              iconName="FolderOpen"
              iconPosition="left"
            >
              Browse Files
            </Button>
            <p className="text-xs text-muted-foreground mt-4">
              Supported format: CSV (comma-separated values)
            </p>
          </>
        ) : (
          <div className="flex items-center justify-between bg-muted rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                <Icon name="FileCheck" size={20} color="var(--color-primary)" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-card-foreground">{selectedFile?.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(selectedFile?.size / 1024)?.toFixed(2)} KB
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-2 rounded-md hover:bg-background transition-colors duration-150"
              aria-label="Remove file"
            >
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>
        )}
      </div>
      {selectedFile && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>
              {uploadMode === 'new' ?'File ready for upload as new play' 
                : selectedPlayId 
                  ? 'File ready to update existing play' :'Please select a play to update'}
            </span>
          </div>
          <Button
            variant="default"
            onClick={handleUpload}
            loading={isProcessing}
            disabled={!canUpload}
            iconName="Upload"
            iconPosition="left"
          >
            {isProcessing ? 'Processing...' : 'Upload & Process'}
          </Button>
        </div>
      )}
      <div className="mt-6 p-4 bg-accent/5 rounded-lg border border-accent/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={18} className="text-accent mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-card-foreground mb-1">CSV Format Requirements:</p>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>Required columns (in order): <code className="bg-muted px-1 rounded text-xs font-mono">gid, act, scene, line_in_scene, speaker, is_stage_direction, text</code></li>
              <li><strong>gid</strong>: Unique integer identifier for each line (e.g., 1001, 1002)</li>
              <li><strong>act</strong>: Act number the line belongs to (e.g., 1, 2, 3)</li>
              <li><strong>scene</strong>: Scene number within the act (e.g., 1, 2, 3)</li>
              <li><strong>line_in_scene</strong>: Line number within the scene for LINE type rows</li>
              <li><strong>speaker</strong>: Character name speaking the line (LINE rows only)</li>
              <li><strong>is_stage_direction</strong>: Line type — <code className="bg-muted px-1 rounded text-xs font-mono">STAGE_DIRECTION</code>, <code className="bg-muted px-1 rounded text-xs font-mono">CHARACTER</code>, or <code className="bg-muted px-1 rounded text-xs font-mono">LINE</code></li>
              <li><strong>text</strong>: The actual dialogue, character name, or stage direction text</li>
            </ul>
            {uploadMode === 'update' && (
              <div className="mt-3 p-2 bg-primary/5 rounded border border-primary/20">
                <p className="font-medium text-primary mb-1">Update Mode Protection:</p>
                <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Duplicate lines (matching gid) will be automatically skipped</li>
                  <li>Existing audio file associations will be preserved</li>
                  <li>Only new lines from the CSV will be added</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVUploadArea;